/**
 * <NotificationBell> — the other half of "what is waiting on me".
 *
 * A task is work you must do and closes when it is done; a notification is
 * something that happened and closes when it is read. Keeping them apart is
 * what lets both badges reach zero: merge them and the work badge never
 * empties (nothing *does* a notification) or the work vanishes when somebody
 * glances at it.
 *
 * So this is deliberately small. It tells you a thing happened, and its only
 * real job is getting you to the place where you can act — which is usually
 * the work panel, one button along.
 *
 * Shared by every host that mounts the shell (the platform Run view, the
 * downloaded runtime, a signed-in public site), driven by an injected
 * `NotificationRuntime` exactly as the work panel is.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  AlertTriangle, Bell, BellOff, CheckCheck, ClipboardList, FileCheck2,
  Inbox, Loader2, X,
} from 'lucide-react'

import type { AppNotification, NotificationRuntime } from './worklist/contract'

export interface NotificationBellProps {
  runtime: NotificationRuntime
  /**
   * Open whatever a notification points at. The bell never navigates itself —
   * that differs per host — so it hands the link back the way the work panel
   * hands back a task.
   */
  onOpenLink?: (link: Record<string, unknown>, notification: AppNotification) => void
  /** Poll interval in ms; 0 disables. Default 60s — half the work panel's
   *  cadence would be noise, since nothing here is urgent by definition. */
  pollMs?: number
}

function iconFor(kind: string) {
  if (kind === 'approval') return FileCheck2
  if (kind === 'task_assigned' || kind === 'task_opened') return ClipboardList
  if (kind === 'task_closed') return CheckCheck
  if (kind === 'alert' || kind === 'job') return AlertTriangle
  return Inbox
}

/** "2 hours ago" — a notification is only ever useful as a distance. */
function whenLabel(iso?: string | null): string {
  if (!iso) return ''
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return ''
  const mins = Math.round((Date.now() - at.getTime()) / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins} min ago`
  const hours = Math.round(mins / 60)
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`
  const days = Math.round(hours / 24)
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`
  return at.toLocaleDateString()
}

export default function NotificationBell({
  runtime, onOpenLink, pollMs = 60000,
}: NotificationBellProps) {
  const [open, setOpen] = useState(false)
  const [unread, setUnread] = useState(0)
  const [items, setItems] = useState<AppNotification[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [muting, setMuting] = useState(false)
  const alive = useRef(true)

  useEffect(() => () => { alive.current = false }, [])

  const refreshCount = useCallback(async () => {
    try {
      const res = await runtime.count()
      if (alive.current) setUnread(res?.unread || 0)
    } catch {
      // A badge that cannot be read stays where it was. Showing zero would
      // say "nothing happened", which is the one thing it must not say
      // when it does not know.
    }
  }, [runtime])

  useEffect(() => { void refreshCount() }, [refreshCount])

  useEffect(() => {
    if (!pollMs) return undefined
    const id = window.setInterval(() => { if (!open) void refreshCount() }, pollMs)
    const onFocus = () => { if (!open) void refreshCount() }
    window.addEventListener('focus', onFocus)
    return () => { window.clearInterval(id); window.removeEventListener('focus', onFocus) }
  }, [refreshCount, pollMs, open])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await runtime.list({ limit: 50 })
      if (!alive.current) return
      setItems(res.items || [])
      setUnread(res.unread || 0)
      setError('')
    } catch (err) {
      if (alive.current) {
        setError(err instanceof Error ? err.message : 'Could not load your notifications.')
      }
    } finally {
      if (alive.current) setLoading(false)
    }
  }, [runtime])

  useEffect(() => { if (open) void load() }, [open, load])

  const markAll = useCallback(async () => {
    try {
      await runtime.markRead()
      setItems((cur) => cur.map((n) => ({ ...n, read: true })))
      setUnread(0)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not mark those read.')
    }
  }, [runtime])

  const activate = useCallback(async (n: AppNotification) => {
    if (!n.read) {
      // Read it optimistically: the click IS the reading, and a row that stays
      // bold after you have opened it reads as a broken badge.
      setItems((cur) => cur.map((r) => (r.notif_id === n.notif_id
        ? { ...r, read: true } : r)))
      setUnread((u) => Math.max(0, u - 1))
      try { await runtime.markRead([n.notif_id]) } catch { void load() }
    }
    if (n.link && onOpenLink) {
      setOpen(false)
      onOpenLink(n.link as Record<string, unknown>, n)
    }
  }, [runtime, onOpenLink, load])

  const mute = useCallback(async (kind: string) => {
    if (!runtime.prefs || !runtime.savePrefs) return
    setMuting(true)
    try {
      const cur = await runtime.prefs()
      const muted = cur.muted_kinds.includes(kind)
        ? cur.muted_kinds.filter((k) => k !== kind)
        : [...cur.muted_kinds, kind]
      await runtime.savePrefs({ ...cur, muted_kinds: muted })
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save that preference.')
    } finally {
      if (alive.current) setMuting(false)
    }
  }, [runtime, load])

  const shown = useMemo(() => items, [items])

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Notifications — things that happened"
        aria-label="Notifications"
        className="tw-tb-icon"
        style={{
          position: 'relative',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 40, height: 40, borderRadius: 10, cursor: 'pointer',
          background: 'transparent',
          color: 'var(--color-text, #0f172a)',
          border: '1px solid transparent',
        }}
      >
        <Bell size={15} />
        {unread > 0 && (
          <span style={{
            position: 'absolute', top: -3, right: -3,
            minWidth: 17, height: 17, padding: '0 4px',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '0.8125rem', fontWeight: 700, lineHeight: 1,
            borderRadius: 9, background: 'var(--color-error, #dc2626)', color: '#fff',
          }}>
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Notifications"
          onClick={() => setOpen(false)}
          style={{
            // Just under the work panel: opening a task from a notification
            // must put the panel on top, not behind.
            position: 'fixed', inset: 0, zIndex: 9420,
            display: 'flex', justifyContent: 'flex-end',
            background: 'rgba(15,23,42,0.25)',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 'min(420px, 100%)', height: '100%',
              display: 'flex', flexDirection: 'column',
              background: 'var(--color-bg, #fff)',
              borderLeft: '1px solid var(--color-border, #e2e8f0)',
              boxShadow: '-12px 0 32px rgba(15,23,42,0.12)',
            }}
          >
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '0.9rem 1rem',
              borderBottom: '1px solid var(--color-border, #e2e8f0)',
            }}>
              <Bell size={17} />
              <strong style={{ fontSize: '1.0125rem' }}>Notifications</strong>
              <span style={{ flex: 1 }} />
              {unread > 0 && (
                <button
                  type="button" onClick={() => { void markAll() }}
                  title="Mark everything read"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                    padding: '0.25rem 0.6rem', borderRadius: 999, cursor: 'pointer',
                    fontSize: '0.8125rem', fontWeight: 700,
                    border: '1px solid var(--color-border, #e2e8f0)',
                    background: 'transparent',
                    color: 'var(--color-text-secondary, #475569)',
                  }}
                >
                  <CheckCheck size={12} /> Mark all read
                </button>
              )}
              <button
                type="button" onClick={() => setOpen(false)} title="Close"
                style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 30, height: 30, borderRadius: 8, cursor: 'pointer',
                  border: '1px solid var(--color-border, #e2e8f0)',
                  background: 'transparent',
                  color: 'var(--color-text-secondary, #475569)',
                }}
              >
                <X size={16} />
              </button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '0.5rem' }}>
              {loading && (
                <div style={centered}><Loader2 size={16} className="spin" /> Loading…</div>
              )}
              {!loading && error && (
                <div style={{ ...centered, color: 'var(--color-error, #b91c1c)' }}>{error}</div>
              )}
              {!loading && !error && shown.length === 0 && (
                <div style={{ ...centered, flexDirection: 'column', gap: 6 }}>
                  <Bell size={18} />
                  Nothing new.
                </div>
              )}
              {shown.map((n) => {
                const Icon = iconFor(n.kind)
                return (
                  <div
                    key={n.notif_id}
                    role="button"
                    tabIndex={0}
                    onClick={() => { void activate(n) }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault(); void activate(n)
                      }
                    }}
                    style={{
                      display: 'flex', gap: 9, padding: '0.6rem 0.65rem',
                      borderRadius: 9, marginBottom: 4, cursor: 'pointer',
                      background: n.read
                        ? 'transparent'
                        : 'var(--color-accent-weak, rgba(76,141,255,0.09))',
                      border: '1px solid var(--color-border, #e2e8f0)',
                    }}
                  >
                    <Icon size={15} style={{ marginTop: 2, flexShrink: 0, opacity: 0.75 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: '0.8925rem', fontWeight: n.read ? 500 : 700,
                      }}>{n.title}</div>
                      {n.body && (
                        <div style={{
                          fontSize: '0.8225rem', marginTop: 2,
                          color: 'var(--color-text-secondary, #64748b)',
                        }}>{n.body}</div>
                      )}
                      <div style={{
                        fontSize: '0.8125rem', marginTop: 4,
                        color: 'var(--color-text-secondary, #94a3b8)',
                      }}>{whenLabel(n.created_at)}</div>
                    </div>
                    {runtime.savePrefs && (
                      <button
                        type="button"
                        disabled={muting}
                        title={`Stop telling me about this kind of thing`}
                        onClick={(e) => { e.stopPropagation(); void mute(n.kind) }}
                        style={{
                          alignSelf: 'flex-start',
                          border: 'none', background: 'transparent', cursor: 'pointer',
                          color: 'var(--color-text-secondary, #94a3b8)', padding: 2,
                        }}
                      >
                        <BellOff size={13} />
                      </button>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

const centered: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
  padding: '2.5rem 1rem', fontSize: '0.9025rem',
  color: 'var(--color-text-secondary, #64748b)',
}
